import { useCallback, useEffect, useState } from "react";
import { money } from "@/lib/api";
import { toast } from "sonner";

const fmt = (iso) => (iso ? String(iso).slice(0, 16).replace("T", " ") : "—");

export function CreditsTab({ c }) {
  const [d, setD] = useState(null);
  const [open, setOpen] = useState(null);
  const [form, setForm] = useState({ email: "", currency: "INR", amount: "", reason: "" });
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => c.credits().then(setD).catch(() => {}), [c]);
  useEffect(() => { load(); }, [load]);

  async function adjust(sign) {
    const amt = Math.round(Number(form.amount) * 100);
    if (!form.email || !amt || form.reason.trim().length < 3) { toast.error("Email, amount and a reason are required"); return; }
    setBusy(true);
    try {
      const r = await c.creditAdjust({ email: form.email.trim(), currency: form.currency, amount_minor: sign * amt, reason: form.reason.trim() });
      toast.success(`Balance for ${form.email.trim().toLowerCase()} is now ${money(r.balance_minor, form.currency)}`);
      setForm({ ...form, amount: "", reason: "" }); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Adjustment failed"); }
    finally { setBusy(false); }
  }

  return (
    <div data-testid="credits-tab">
      <div className="kpis" style={{ gridTemplateColumns: "repeat(3,1fr)", marginBottom: 16 }}>
        <div className="kpi"><b data-testid="credits-total-inr">{money(d?.total_inr || 0, "INR")}</b><span>outstanding INR credit</span></div>
        <div className="kpi"><b>{money(d?.total_usd || 0, "USD")}</b><span>outstanding USD credit</span></div>
        <div className="kpi"><b>{d ? d.credits.filter((r) => r.balance_minor > 0).length : "–"}</b><span>clients with a balance</span></div>
      </div>

      <div className="panel-soft" style={{ padding: 22, marginBottom: 16 }} data-testid="credit-adjust">
        <h3>Manual adjustment</h3>
        <p className="muted small" style={{ margin: "6px 0 12px" }}>Add goodwill credit or remove credit that was granted by mistake. Every change is logged with your email and reason and is visible to the client on their jobs page.</p>
        <div className="row">
          <div className="field"><label>Client email</label><input data-testid="adjust-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="client@company.com" /></div>
          <div className="field"><label>Currency</label><select data-testid="adjust-currency" value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value })}><option>INR</option><option>USD</option></select></div>
        </div>
        <div className="row">
          <div className="field"><label>Amount ({form.currency === "INR" ? "₹" : "$"})</label><input type="number" min="1" step="1" data-testid="adjust-amount" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} placeholder="500" /></div>
          <div className="field"><label>Reason</label><input data-testid="adjust-reason" value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} placeholder="Goodwill for late report on 99T-2418" /></div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button className="btn sm green" data-testid="adjust-add" disabled={busy} onClick={() => adjust(1)}>Add credit</button>
          <button className="btn sm ghost" data-testid="adjust-remove" disabled={busy} onClick={() => adjust(-1)}>Remove credit</button>
        </div>
      </div>

      <div className="panel-soft" style={{ overflow: "auto" }}>
        <table className="tbl"><thead><tr><th>Client</th><th>Jobs</th><th className="c">Balance</th><th>Entries</th><th>Last change</th><th></th></tr></thead>
          <tbody>{(d?.credits || []).map((r) => (
            <tr key={r.id} data-testid={`credit-row-${r.email}`}>
              <td>{r.email}</td><td>{r.jobs}</td>
              <td className="c num"><b style={{ color: r.balance_minor > 0 ? "var(--green)" : undefined }}>{money(r.balance_minor, r.currency)}</b></td>
              <td>{r.ledger.length}</td><td className="small">{fmt(r.ledger[0]?.at)}</td>
              <td><button className="btn sm ghost" data-testid={`credit-ledger-${r.email}`} onClick={() => { setOpen(open === r.id ? null : r.id); setForm({ ...form, email: r.email, currency: r.currency }); }}>{open === r.id ? "Hide ledger" : "Ledger"}</button></td>
            </tr>
          )).flatMap((row, i) => {
            const r = d.credits[i];
            if (open !== r.id) return [row];
            return [row, (
              <tr key={r.id + "-ledger"}><td colSpan={6} style={{ background: "var(--paper)" }}>
                <table className="tbl" data-testid="credit-ledger-table"><thead><tr><th>When</th><th>Type</th><th>Job</th><th>Reason</th><th>By</th><th className="c">Amount</th></tr></thead>
                  <tbody>{r.ledger.map((e) => <tr key={e.id}><td className="small">{fmt(e.at)}</td><td><span className={`pill ${e.type === "credit" ? "" : "neutral"}`}>{e.type}</span></td><td>{e.job_code || "—"}</td><td>{e.reason}</td><td className="small">{e.actor}</td>
                    <td className="c num" style={{ color: e.amount_minor < 0 ? "var(--fail)" : "var(--green)" }}>{e.amount_minor < 0 ? "−" : "+"}{money(Math.abs(e.amount_minor), r.currency)}</td></tr>)}</tbody></table>
              </td></tr>
            )];
          })}</tbody></table>
        {d && d.credits.length === 0 && <div className="empty"><b>No credits yet</b>Credits appear when sessions go unfilled or you add one manually.</div>}
      </div>
    </div>
  );
}
